import { useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getAdminOrders, getAdminInvoices, getAdminSubscriptions } from "@/services/adminService";
import type { ErpOrder, ErpInvoice, AdminSubscription } from "@/types/admin";
import { demoAdminRevenueSeries } from "@/mocks/admin";
import { useAdminResource } from "@/pages/portal/admin/hooks/useAdminResource";
import AdminDataError from "@/pages/portal/admin/components/AdminDataError";
import AdminStatCard from "@/pages/portal/admin/components/AdminStatCard";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";

const C_PRIMARY = "oklch(0.42 0.12 265)";
const C_ACCENT = "oklch(0.76 0.152 79)";
const C_SECONDARY = "oklch(0.582 0.092 175)";
const C_EMERALD = "oklch(0.6 0.15 155)";
const C_ROSE = "oklch(0.55 0.19 15)";

const fmt = (n: number) => n.toLocaleString("en-IN");
const inr = (n: number) => `₹${fmt(Math.round(n))}`;

interface FinanceData {
  orders: ErpOrder[];
  invoices: ErpInvoice[];
  subs: AdminSubscription[];
}

export default function FinanceSection() {
  const { accessToken } = useAuth();
  const { data, loading, error, reload } = useAdminResource<FinanceData>(
    () =>
      Promise.all([
        getAdminOrders(accessToken),
        getAdminInvoices(accessToken),
        getAdminSubscriptions(accessToken),
      ]).then(([orders, invoices, subs]) => ({ orders, invoices, subs })),
    { orders: [], invoices: [], subs: [] },
    [accessToken],
  );

  const kpis = useMemo(() => {
    const activeSubs = data.subs.filter((s) => s.status === "active" || s.status === "expiring");
    const arr = activeSubs.reduce((s, x) => s + x.amount, 0);
    const collected = data.invoices.filter((i) => i.status === "paid").reduce((s, i) => s + i.total, 0);
    const outstanding = data.invoices.filter((i) => i.status === "sent" || i.status === "overdue").reduce((s, i) => s + i.total, 0);
    const totalRevenue = demoAdminRevenueSeries.reduce((s, r) => s + r.revenue, 0);
    return { arr, mrr: Math.round(arr / 12), collected, outstanding, totalRevenue };
  }, [data]);

  const revenueByPlan = useMemo(() => {
    const map = new Map<string, number>();
    data.orders
      .filter((o) => o.status === "paid" || o.status === "fulfilled" || o.status === "processing")
      .forEach((o) => map.set(o.plan, (map.get(o.plan) ?? 0) + o.amount));
    return Array.from(map, ([name, value]) => ({ name, value }));
  }, [data.orders]);

  if (!loading && error) return <AdminDataError message={error} onRetry={reload} />;

  const BAR_COLORS = [C_PRIMARY, C_ACCENT, C_SECONDARY, C_EMERALD, C_ROSE];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <AdminStatCard label="Revenue (12 months)" value={loading ? "—" : inr(kpis.totalRevenue)} icon="ri-line-chart-line" accent="primary" sub="gross income" />
        <AdminStatCard label="Monthly Recurring" value={loading ? "—" : inr(kpis.mrr)} icon="ri-refresh-line" accent="accent" sub="MRR from active plans" />
        <AdminStatCard label="Annual Recurring" value={loading ? "—" : inr(kpis.arr)} icon="ri-calendar-line" accent="emerald" sub="ARR from active plans" />
        <AdminStatCard label="Outstanding" value={loading ? "—" : inr(kpis.outstanding)} icon="ri-hourglass-line" accent="amber" sub={`${inr(kpis.collected)} collected`} />
      </div>

      {/* Revenue vs expenses */}
      <div className="rounded-xl border border-background-200 bg-background-50 p-6">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-heading text-sm font-bold text-foreground-950">Revenue vs Expenses</h3>
            <p className="mt-0.5 text-xs text-foreground-500">Trailing 12 months of income and operating cost.</p>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-xs">
            <span className="flex items-center gap-1.5 text-foreground-600"><span className="inline-block h-2.5 w-2.5 rounded-full bg-primary-500" /> Revenue</span>
            <span className="flex items-center gap-1.5 text-foreground-600"><span className="inline-block h-2.5 w-2.5 rounded-full bg-rose-400" /> Expenses</span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={demoAdminRevenueSeries} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
            <defs>
              <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C_PRIMARY} stopOpacity={0.28} />
                <stop offset="100%" stopColor={C_PRIMARY} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C_ROSE} stopOpacity={0.2} />
                <stop offset="100%" stopColor={C_ROSE} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-background-200, #e5e7eb)" />
            <XAxis dataKey="month" tick={{ fontSize: 10, fill: "var(--color-foreground-500, #6b7280)" }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 10, fill: "var(--color-foreground-500, #6b7280)" }} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }} formatter={(v: number) => inr(v)} />
            <Area type="monotone" dataKey="revenue" stroke={C_PRIMARY} strokeWidth={2.5} fill="url(#gRev)" />
            <Area type="monotone" dataKey="expenses" stroke={C_ROSE} strokeWidth={2} fill="url(#gExp)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        {/* Revenue by plan */}
        <div className="rounded-xl border border-background-200 bg-background-50 p-6">
          <h3 className="mb-1 font-heading text-sm font-bold text-foreground-950">Revenue by Plan</h3>
          <p className="mb-5 text-xs text-foreground-500">Collected and in-flight order value per listing plan.</p>
          {revenueByPlan.length === 0 ? (
            <div className="flex h-[220px] items-center justify-center text-sm text-foreground-400">No order data yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={revenueByPlan} margin={{ top: 4, right: 8, left: -12, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-background-200, #e5e7eb)" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "var(--color-foreground-600, #4b5563)" }} tickLine={false} axisLine={false} angle={-20} textAnchor="end" interval={0} tickFormatter={(v: string) => v.split(" ")[0]} />
                <YAxis tick={{ fontSize: 10, fill: "var(--color-foreground-500, #6b7280)" }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }} formatter={(v: number) => [inr(v), "Revenue"]} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {revenueByPlan.map((_e, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Collections summary */}
        <div className="rounded-xl border border-background-200 bg-background-50">
          <div className="border-b border-background-200 px-6 py-4">
            <h3 className="font-heading text-sm font-bold text-foreground-950">Collections Summary</h3>
            <p className="mt-0.5 text-xs text-foreground-500">Invoice settlement across all billing, live.</p>
          </div>
          <div className="divide-y divide-background-100">
            {[
              { label: "Paid invoices", value: inr(kpis.collected), icon: "ri-check-double-line", tone: "text-emerald-600" },
              { label: "Outstanding (sent + overdue)", value: inr(kpis.outstanding), icon: "ri-hourglass-line", tone: "text-amber-600" },
              { label: "Active subscriptions", value: fmt(data.subs.filter((s) => s.status === "active" || s.status === "expiring").length), icon: "ri-bank-card-line", tone: "text-primary-600" },
              { label: "Orders in system", value: fmt(data.orders.length), icon: "ri-shopping-bag-3-line", tone: "text-secondary-600" },
            ].map((row) => (
              <div key={row.label} className="flex items-center gap-4 px-6 py-4">
                <span className={`flex h-9 w-9 items-center justify-center rounded-lg bg-background-100 ${row.tone}`}>
                  <i className={`${row.icon} text-lg`} />
                </span>
                <span className="flex-1 text-sm text-foreground-700">{row.label}</span>
                <span className="text-sm font-bold tabular-nums text-foreground-950">{loading ? "—" : row.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}